import { useEffect, useState } from "react";

// localStorage key shared across portal + admin tabs so activity in any
// signed-in tab keeps the others alive.
const STORAGE_KEY = "forte-last-activity";

interface Options {
  idleMinutes: number;
  warnSecondsBefore?: number;
  onIdle: () => void;
}

interface State {
  warning: boolean;
  /** Seconds remaining until forced sign-out, while in warning state. */
  secondsLeft: number;
  /** Call to dismiss the warning + reset the idle timer. */
  stayActive: () => void;
}

export function useIdleTimeout({
  idleMinutes,
  warnSecondsBefore = 30,
  onIdle,
}: Options): State {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    const limitMs = idleMinutes * 60_000;
    const warnMs = warnSecondsBefore * 1000;

    // Seed with "now" so a freshly-loaded page doesn't immediately warn.
    const reset = () => {
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); }
      catch { /* private mode / quota; ignore */ }
      setWarning(false);
    };
    reset();

    // Any of these events counts as activity. `passive` keeps scroll smooth.
    const events: (keyof WindowEventMap)[] = [
      "mousedown", "keydown", "touchstart", "scroll", "wheel", "click",
    ];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    // Polling once per second is plenty for a minute-scale timer and keeps
    // the warning countdown accurate.
    const tick = setInterval(() => {
      const last = Number(localStorage.getItem(STORAGE_KEY) || Date.now());
      const idle = Date.now() - last;
      if (idle >= limitMs) {
        clearInterval(tick);
        onIdle();
      } else if (idle >= limitMs - warnMs) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((limitMs - idle) / 1000)));
      } else {
        setWarning(false);
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearInterval(tick);
    };
  }, [idleMinutes, warnSecondsBefore, onIdle]);

  return {
    warning,
    secondsLeft,
    stayActive: () => {
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); }
      catch { /* ignore */ }
      setWarning(false);
    },
  };
}
